import React, { ChangeEvent, useState } from 'react';

import { Button, Form, Input, Label, Modal, Text, toastService } from '@thefarmersfront/kitchen';

import { useShallow } from 'zustand/react/shallow';

import { InfoText } from '~/components/common';

import { fetchSkusProductCode } from '~/hooks/product';
import { useProductStore } from '~/store';

import { PRODUCTS, TOAST_MESSAGE } from '~/constants';

import type { TypeInputState } from '~/types/common.types';
import type { TypeSkuProductCodeData } from '~/types/product.types';

import styles from './ProductDetailsModalStockInfoSectionSearchProductCodeModal.module.scss';

const ProductDetailsModalStockInfoSectionSearchProductCodeModal = ({
  open,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  onCancel: () => void | Promise<void>;
  onConfirm: () => void | Promise<void>;
}) => {
  const [productDetailModalValue, setProductDetailModalValue] = useProductStore(
    useShallow((state) => [state.productDetailModalValue, state.setProductDetailModalValue])
  );

  const [isConfirmButtonDisabled, setIsConfirmButtonDisabled] = useState(true);
  const [productCodeInputValue, setProductCodeInputValue] = useState('');
  const [productCodeInputState, setProductCodeInputState] = useState<TypeInputState>({
    status: 'default',
  });
  const [productInfo, setProductInfo] = useState<TypeSkuProductCodeData | null>(null);

  const handleClickConfirmModal = () => {
    setProductDetailModalValue({
      ...productDetailModalValue,
      productCode: productCodeInputValue,
    });

    onConfirm();
  };

  const handleChangeProductCodeInput = (e: ChangeEvent<HTMLInputElement>) => {
    setProductCodeInputState({ status: 'default' });
    setProductCodeInputValue(e.target.value);
  };

  const handleClickProductCodeInput = () => {
    setProductCodeInputState({ status: 'default' });
  };

  const handleClickSearchCodeButton = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setProductInfo(null);

    if (productCodeInputValue.length < 12) {
      setProductCodeInputState({
        status: 'error',
        message: '출하코드 12자 입력 후 검색해주세요.',
      });

      return;
    }

    fetchSkuByProductCode(productCodeInputValue);
  };

  const fetchSkuByProductCode = async (skuCode: string) => {
    try {
      const data = await fetchSkusProductCode(skuCode);

      if (data && !!data.skuCode) {
        setProductInfo(data);
        setIsConfirmButtonDisabled(false);
      } else {
        setProductInfo(null);
        setIsConfirmButtonDisabled(true);
        setProductCodeInputState({
          status: 'error',
          message: '일치하는 상품 정보가 없습니다.',
        });
      }
    } catch (error: any) {
      if (error?.code === PRODUCTS.SKUCODE_NOT_FOUND) {
        setIsConfirmButtonDisabled(true);
        setProductCodeInputState({
          status: 'error',
          message: '일치하는 상품 정보가 없습니다.',
        });

        return;
      }

      toastService.addToast({
        type: 'error',
        message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR,
      });
    }
  };

  return (
    <Modal
      open={open}
      title="출하코드 검색"
      confirmText="등록"
      cancelText="취소"
      size="medium"
      disabled={isConfirmButtonDisabled}
      onCancel={onCancel}
      onConfirm={handleClickConfirmModal}
    >
      <div className={styles['product-code-search-modal']}>
        <Form className={styles['search-input-row']} onSubmit={handleClickSearchCodeButton}>
          <Input
            className={styles['input']}
            type="text"
            size="small"
            placeholder="출하코드 12자 입력"
            value={productCodeInputValue}
            status={productCodeInputState.status}
            validationMessage={productCodeInputState.message ?? ''}
            onClick={handleClickProductCodeInput}
            onChange={handleChangeProductCodeInput}
          />
          <Button size="small" color="secondary" type="submit">
            검색
          </Button>
        </Form>
        {productInfo && (
          <div className={styles['product-info-wrapper']}>
            <div className={styles['product-info-row']}>
              <Label className={styles['label']} title="마스터코드" />
              <Text size="large">{productInfo?.skuCode}</Text>
            </div>
            <div className={styles['product-info-row']}>
              <Label className={styles['label']} title="대체(88)코드" />
              <Text size="large">{productInfo?.alternativeCode}</Text>
            </div>
            <div className={styles['product-info-row']}>
              <Label className={styles['label']} title="상품명" />
              <Text size="large">{productInfo?.skuName}</Text>
            </div>
            <div className={styles['product-info-row']}>
              <Label className={styles['label']} title="현재상품명" />
              <Text size="large">{productDetailModalValue.skuName}</Text>
            </div>
          </div>
        )}
        <div className={styles['information-message']}>
          <InfoText>본품인 경우 최초 등록되는 상품코드가 출하코드로 자동 설정됩니다.</InfoText>
          <InfoText>
            {`대품(동일한 실물로 기등록 되어있고 이벤트 등으로 추가 등록하는 상품)인 경우 출하코드는\n본품의 마스터코드와 동일하게 입력해주세요.`}
          </InfoText>
        </div>
      </div>
    </Modal>
  );
};

export default ProductDetailsModalStockInfoSectionSearchProductCodeModal;
